import _ from 'lodash'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import isoWeek from 'dayjs/plugin/isoWeek'
import relativeTime from 'dayjs/plugin/relativeTime'
import React, { useEffect, useMemo, useState } from 'react'
import { Controller, SubmitHandler, UseFormReturn, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { LoadingButton } from '@mui/lab'
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, Stack, Tooltip, Typography, Switch, Button } from '@mui/material'
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import EventNoteIcon from '@mui/icons-material/EventNote'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import TimelapseIcon from '@mui/icons-material/Timelapse'

import colors from '@/assets/scss/_themes-vars.module.scss'

import { ScheduleRecurrenceType } from '@/core/models/schedule.model'

import CheckboxInputController from '@/components/ui/inputs/CheckboxInputController'
import DatePickerInput from '@/components/ui/inputs/DatePickerInput'
import RadioGroupInput from '@/components/ui/inputs/RadioGroupInput'
import { SelectOption } from '@/components/ui/inputs/SelectInput'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import TextInput from '@/components/ui/inputs/TextInput'
import TimePickerInput from '@/components/ui/inputs/TimePickerInput'

import { useLanguage } from '@/hooks/useLanguage'

import { toAdapterLocale, thresholds } from '@/utils/locale'
import TextInputController from '@/components/ui/inputs/TextInputController'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

dayjs.extend(duration)
dayjs.extend(isoWeek)
// `delete relativeTime.$i` used to live here as a re-install workaround,
// but ESM/Vite throws on `delete` against a frozen module namespace —
// silently aborting this whole block and leaving `humanize()` broken
// (renders crash with "fromNow is not a function"). The plugin is
// idempotent; calling extend twice across modules is a no-op so the
// thresholds passed here win unless someone else extends with different
// thresholds later.
dayjs.extend(relativeTime, {
  thresholds,
})

const schema = z.object({
  consultationReason: z.string().min(1),
  duration: z.number().min(15),
  isRecurring: z.boolean(),
  startDate: z.string().min(1),
  endDate: z.string().optional(),
  startTime: z.number().positive(),
  endTime: z.number().positive(),
  recurrenceRule: z.object({
    daysOfWeek: z.array(z.number()),
    datesOfMonth: z.array(z.number()),
    monthsOfYear: z.array(z.number()),
    recurrenceType: z.nativeEnum(ScheduleRecurrenceType),
    separationCount: z.number().min(1),
  }),
})

type AddScheduleFormData = z.infer<typeof schema>

type ScheduleDialogProps = {
  isSubmitting?: boolean
  open: boolean
  onClose: () => void
  onSubmit: SubmitHandler<AddScheduleFormData>
  specialityOptions: SelectOption[]
}

export function AddScheduleDialog(props: ScheduleDialogProps) {
  const { isSubmitting = false, open, onClose, onSubmit, specialityOptions } = props

  const { t } = useTranslation()

  const isMobile = useIsMobile()

  const defaultStartTime = useMemo(() => Math.ceil(dayjs().diff(dayjs().startOf('day'), 'minutes') / 15) * 15, [open])

  const rhForm = useForm<AddScheduleFormData>({
    defaultValues: {
      consultationReason: '',
      duration: 15,
      isRecurring: true,
      startDate: dayjs().format('YYYY-MM-DD'),
      startTime: defaultStartTime,
      endTime: defaultStartTime + 30,
      recurrenceRule: {
        daysOfWeek: [],
        datesOfMonth: [],
        monthsOfYear: [],
        recurrenceType: ScheduleRecurrenceType.DAILY,
        separationCount: 1,
      },
    },
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (!open) rhForm.reset()
  }, [open, rhForm])

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}>
        {t('dialog.addSchedule.title')}
        <Tooltip title={t('common:button.close.tooltip')}>
          <IconButton onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent dividers>
        <form onSubmit={rhForm.handleSubmit(onSubmit)}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* General Section */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', fontWeight: 'bold' }}>
                {t('common:label.general', 'General')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextInputController control={rhForm.control} fullWidth name="consultationReason" label={t('field.consultationReason.label')} placeholder={t('field.consultationReason.label')} />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Timing Section */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', fontWeight: 'bold' }}>
                {t('common:label.timing', 'Timing')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Controller
                    control={rhForm.control}
                    name="startDate"
                    render={({ field }) => (
                      <DatePickerInput
                        label={t('field.startDate.label', 'Start Date')}
                        onChange={(date) => {
                          rhForm.setValue('startDate', date?.format('YYYY-MM-DD') ?? '')
                        }}
                        value={field.value && dayjs(field.value).isValid() ? dayjs(field.value) : null}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DurationField rhForm={rhForm} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    control={rhForm.control}
                    name="startTime"
                    render={({ field }) => (
                      <TimePickerInput
                        fullWidth
                        label={t('field.startTime.label', 'Start Time')}
                        onChange={(time) => {
                          if (dayjs.isDayjs(time)) {
                            rhForm.setValue('startTime', time.diff(dayjs().startOf('day'), 'minutes'))
                          }
                        }}
                        value={dayjs().startOf('day').add(field.value, 'minutes')}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    control={rhForm.control}
                    name="endTime"
                    render={({ field }) => (
                      <TimePickerInput
                        fullWidth
                        label={t('field.endTime.label', 'End Time')}
                        onChange={(time) => {
                          if (dayjs.isDayjs(time)) {
                            rhForm.setValue('endTime', time.diff(dayjs().startOf('day'), 'minutes'))
                          }
                        }}
                        value={dayjs().startOf('day').add(field.value, 'minutes')}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Recurrence Section */}
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
                  {t('dialog.addSchedule.form.field.isRecurring.label')}
                </Typography>
                <Controller
                  name="isRecurring"
                  control={rhForm.control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  )}
                />
              </Stack>

              <Box sx={{ pl: 0 }}>
                <ScheduleRecurrenceRule rhForm={rhForm} />
              </Box>
            </Box>
          </Stack>
        </form>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">
          {t('common:button.cancel.label', 'Cancel')}
        </Button>
        <LoadingButton data-testid="add-schedule-submit" loading={isSubmitting} color="primary" disableElevation onClick={rhForm.handleSubmit(onSubmit)} variant="contained">
          {t('common:button.submit.label')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

function ScheduleRecurrenceRule({ rhForm }: { rhForm: UseFormReturn<AddScheduleFormData> }) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const isRecurring = useWatch({ control: rhForm.control, name: 'isRecurring' })

  const recurrenceTypeOptions = useMemo(
    () => [
      { label: t('dialog.addSchedule.recurrenceType.daily'), value: ScheduleRecurrenceType.DAILY },
      { label: t('dialog.addSchedule.recurrenceType.weekly'), value: ScheduleRecurrenceType.WEEKLY },
    ],
    [t]
  )

  if (!isRecurring) return null

  return (
    <Stack spacing={2}>
      <Stack direction={isMobile ? "column" : "row"} alignItems="center" justifyContent="start" spacing={1}>
        <Box component="span" sx={{ color: colors.gray700, fontSize: '14px', fontWeight: 500, whiteSpace: 'nowrap' }}>
          {t('dialog.addSchedule.form.field.separateCount.label')}
        </Box>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
          <Box sx={{ width: isMobile ? 80 : 150 }}>
            <SeparateCountField rhForm={rhForm} />
          </Box>
          <SelectInputController control={rhForm.control} fullWidth name="recurrenceRule.recurrenceType" options={recurrenceTypeOptions} />
        </Stack>
      </Stack>
      <DaysOfWeekField rhForm={rhForm} />
      <DaysOfMonthField rhForm={rhForm} />
      <MonthsOfYearField rhForm={rhForm} />
      <EndDateField rhForm={rhForm} />
    </Stack>
  )
}

function DaysOfWeekField({ rhForm }: { rhForm: UseFormReturn<AddScheduleFormData> }) {
  const language = useLanguage()
  const { t } = useTranslation()
  const daysOfWeekOptions = useMemo(
    () => _.range(7).map((n) => ({ label: dayjs().isoWeekday(n).locale(toAdapterLocale(language)).format('dddd'), value: n })),
    [language]
  )
  const [daysOfWeek, recurrenceType] = useWatch({ control: rhForm.control, name: ['recurrenceRule.daysOfWeek', 'recurrenceRule.recurrenceType'] })

  useEffect(() => {
    if (recurrenceType !== ScheduleRecurrenceType.WEEKLY && daysOfWeek.length !== 0) {
      rhForm.setValue('recurrenceRule.daysOfWeek', [])
    } else if (recurrenceType === ScheduleRecurrenceType.WEEKLY && daysOfWeek.length === 0) {
      rhForm.setValue('recurrenceRule.daysOfWeek', [1])
    }
  }, [daysOfWeek, recurrenceType, rhForm])

  if (recurrenceType !== ScheduleRecurrenceType.WEEKLY) return null

  return (
    <SelectInputController<AddScheduleFormData>
      control={rhForm.control}
      enableCheckmark
      label={t('dialog.addSchedule.form.field.daysOfWeek.label')}
      multiple
      name="recurrenceRule.daysOfWeek"
      options={daysOfWeekOptions}
    />
  )
}

function DaysOfMonthField({ rhForm }: { rhForm: UseFormReturn<AddScheduleFormData> }) {
  const { t } = useTranslation()

  const daysOfMonthOptions = useMemo(() => _.range(31).map((n) => ({ label: `${n + 1}`, value: n + 1 })), [])
  const [startDate, datesOfMonth, recurrenceType] = useWatch({
    control: rhForm.control,
    name: ['startDate', 'recurrenceRule.datesOfMonth', 'recurrenceRule.recurrenceType'],
  })

  useEffect(() => {
    if (recurrenceType !== ScheduleRecurrenceType.MONTHLY && datesOfMonth.length !== 0) {
      rhForm.setValue('recurrenceRule.datesOfMonth', [])
    } else if (recurrenceType === ScheduleRecurrenceType.MONTHLY && datesOfMonth.length === 0) {
      rhForm.setValue('recurrenceRule.datesOfMonth', [dayjs(startDate).date()])
    }
  }, [datesOfMonth, recurrenceType, rhForm, startDate])

  if (recurrenceType !== ScheduleRecurrenceType.MONTHLY) return null

  return (
    <SelectInputController<AddScheduleFormData>
      control={rhForm.control}
      enableCheckmark
      label={t('dialog.addSchedule.form.field.datesOfMonth.label')}
      multiple
      name="recurrenceRule.datesOfMonth"
      options={daysOfMonthOptions}
    />
  )
}

function DurationField({ rhForm }: { rhForm: UseFormReturn<AddScheduleFormData> }) {
  const { t } = useTranslation()

  // Static labels instead of dayjs.duration(...).humanize(): under Vite's dep
  // optimization a duplicated dayjs copy can leave relativeTime unattached,
  // making humanize() throw "fromNow is not a function" and crashing the whole
  // dialog on first render. The labels are short and clearer this way.
  const options = useMemo(
    () => [15, 30, 45, 60].map((value) => ({ label: value % 60 === 0 ? `${value / 60} h` : `${value} min`, value })),
    []
  )
  return <SelectInputController<AddScheduleFormData> control={rhForm.control} fullWidth name="duration" options={options} label={t('dialog.addSchedule.form.field.duration.label')} />
}

function EndDateField({ rhForm }: { rhForm: UseFormReturn<AddScheduleFormData> }) {
  const { t } = useTranslation()

  const [selectedOption, setSelectedOption] = useState<'never' | 'on'>('never')

  const options: Array<{ label: React.ReactNode; value: typeof selectedOption }> = useMemo(
    () => [
      { label: t('dialog.addSchedule.form.field.endDate.never'), value: 'never' },
      {
        label: (
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={6}>
            <span>{t('dialog.addSchedule.form.field.endDate.on')}</span>
            <Controller
              control={rhForm.control}
              name="endDate"
              render={({ field }) => (
                <DatePickerInput
                  disabled={selectedOption !== 'on'}
                  onChange={(date) => {
                    rhForm.setValue('endDate', date?.format('YYYY-MM-DD') ?? '')
                  }}
                  value={field.value && dayjs(field.value).isValid() ? dayjs(field.value) : null}
                />
              )}
            />
          </Stack>
        ),
        value: 'on',
      },
    ],
    [rhForm, selectedOption]
  )

  return (
    <RadioGroupInput
      label={t('dialog.addSchedule.form.field.endDate.label')}
      onChange={(_event, value) => {
        if (value === 'never') rhForm.resetField('endDate')
        setSelectedOption(value as typeof selectedOption)
      }}
      options={options}
      value={selectedOption}
      sx={{ '& .MuiFormLabel-root': { fontWeight: 500 } }}
    />
  )
}

function MonthsOfYearField({ rhForm }: { rhForm: UseFormReturn<AddScheduleFormData> }) {
  const language = useLanguage()
  const { t } = useTranslation()

  const monthsOfYearOptions = useMemo(
    () => _.range(12).map((n) => ({ label: dayjs().month(n).locale(toAdapterLocale(language)).format('MMMM'), value: n })),
    [language]
  )
  const [startDate, monthsOfYear, recurrenceType] = useWatch({
    control: rhForm.control,
    name: ['startDate', 'recurrenceRule.monthsOfYear', 'recurrenceRule.recurrenceType'],
  })

  useEffect(() => {
    if (recurrenceType !== ScheduleRecurrenceType.YEARLY && monthsOfYear.length !== 0) {
      rhForm.setValue('recurrenceRule.monthsOfYear', [])
    } else if (recurrenceType === ScheduleRecurrenceType.YEARLY && monthsOfYear.length === 0) {
      rhForm.setValue('recurrenceRule.monthsOfYear', [dayjs(startDate).month()])
    }
  }, [monthsOfYear, recurrenceType, rhForm, startDate])

  if (recurrenceType !== ScheduleRecurrenceType.YEARLY) return null

  return (
    <SelectInputController<AddScheduleFormData>
      control={rhForm.control}
      enableCheckmark
      label={t('dialog.addSchedule.form.field.monthsOfYear.label')}
      multiple
      name="recurrenceRule.monthsOfYear"
      options={monthsOfYearOptions}
    />
  )
}

function SeparateCountField({ rhForm }: { rhForm: UseFormReturn<AddScheduleFormData> }) {
  const field = rhForm.register('recurrenceRule.separationCount', { valueAsNumber: true })
  return <TextInput {...field} error={!!rhForm.formState.errors.recurrenceRule?.separationCount} inputProps={{ min: 1, step: 1 }} type="number" />
}
